import { useState, useEffect, useCallback, useMemo } from 'react'
import { Sparkles, Play, Plus, Check, Loader2, RefreshCw, Wand2 } from 'lucide-react'
import { useHighlightsApi } from './useHighlightsApi'
import { useProfileMomentTypes } from './useProfileMomentTypes'
import ClipPreview from './ClipPreview'
import RobotsApplyModal from './RobotsApplyModal'
import { dedupAiKeywordAgainstLlm } from './dedupMoments'
import { useReject } from './useReject'
import { RejectMomentDialog, RejectUndoToast, RejectButton } from './RejectMomentDialog'
import { useApprove } from './useApprove'
import { ApproveButton, ApproveUndoToast } from './ApproveMomentControl'

// Step 26: stable fingerprint for client-side locally-dropped tracking.
// Lets us hide a rejected moment immediately without refetching, AND
// re-show it if the editor hits Undo on the toast.
const dropFingerprint = (m) => `${m.tbtv_id}|${m.start_ms}|${(m.title || '').trim().toLowerCase()}`

// Cross-catalog list of top Mux Robots Find Key Moments. Sorted by Mux's
// quality_score (0–1). Each row links into the existing ClipPreview modal
// and supports "Add to reel" so editors can curate from AI-surfaced moments.

function fmtTime(ms) {
  const sec = Math.floor((ms || 0) / 1000)
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = sec % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`
}

function scoreColor(score) {
  if (score == null) return '#888'
  if (score >= 0.85) return '#22c55e'
  if (score >= 0.7)  return '#a78bfa'
  if (score >= 0.5)  return '#3b82f6'
  return '#888'
}

// Chip vocabulary now comes from the sport-profile registry — see
// useProfileMomentTypes(profileKey). Hockey's seeded row reproduces the
// old hardcoded list exactly (same 10 entries in the same order + colors),
// so behavior for hockey videos is unchanged. Football and future sports
// supply their own moment_types_json.

// Source filter chip definitions. 'mux' = Mux Robots Find Key Moments;
// the two TBTV-AI variants split by detector. Editors flip these when
// auditing what a specific layer found.
const SOURCE_META = [
  { key: 'mux',             label: 'Mux',                color: '#fb923c' },
  { key: 'tbtv_ai_keyword', label: 'TBTV-AI keyword',    color: '#a78bfa' },
  { key: 'tbtv_ai_llm',     label: 'TBTV-AI LLM',        color: '#22c55e' },
]

// Per-card pill colors used on each moment to tag its origin.
const SOURCE_PILL = {
  mux:    { label: 'Mux',    color: '#fb923c', bg: 'rgba(251,146,60,0.15)' },
  keyword:{ label: 'AI · keyword', color: '#a78bfa', bg: 'rgba(167,139,250,0.15)' },
  llm:    { label: 'AI · LLM',     color: '#22c55e', bg: 'rgba(34,197,94,0.15)' },
}
function sourcePill(m) {
  if (m.source === 'mux') return SOURCE_PILL.mux
  if (m.source === 'tbtv_ai') return SOURCE_PILL[m.detector] || SOURCE_PILL.llm
  return null
}

export default function KeyMomentsExplorer({ reelCart, profileKey = 'hockey', profiles = [] }) {
  // Chip vocabulary for this sport profile. Hockey fallback is baked in
  // so first-paint before the fetch resolves still renders the correct
  // 10 chips in the correct order + colors (matches the old hardcoded
  // TYPE_META byte-for-byte until Phase 5 seeds a different profile).
  const { momentTypes: TYPE_META } = useProfileMomentTypes(profileKey)
  const api = useHighlightsApi()
  const [minScore, setMinScore] = useState(0.7)
  const [selectedTypes, setSelectedTypes] = useState(() => new Set()) // empty = all
  const [selectedSources, setSelectedSources] = useState(() => new Set()) // empty = all
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [previewing, setPreviewing] = useState(null) // { tbtv_id, startMs, endMs, title }
  const [applyOpen, setApplyOpen] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setErr(null)
    try {
      const r = await api.keyMomentsList({
        minScore,
        limit: 100,
        // Endpoint whitelists known types; '(other)' filters server-side
        // to rows with NULL moment_type via a special path.
        momentType: selectedTypes.size > 0 ? Array.from(selectedTypes) : null,
        source: selectedSources.size > 0 ? Array.from(selectedSources) : null,
      })
      setData(r)
    } catch (e) {
      setErr(e.message)
    } finally {
      setLoading(false)
    }
  }, [api, minScore, selectedTypes, selectedSources])

  useEffect(() => { load() }, [load])

  function toggleType(key) {
    setSelectedTypes(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }
  function clearTypes() { setSelectedTypes(new Set()) }
  function toggleSource(key) {
    setSelectedSources(prev => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key); else next.add(key)
      return next
    })
  }
  function clearSources() { setSelectedSources(new Set()) }

  // Step 25g: drop AI-keyword moments that fall inside an AI-LLM moment's
  // span (visible duplicates of the same play). Editor can disable LLM in
  // the source-filter chips to see the raw KW rows again.
  const dedupedMoments = useMemo(() => dedupAiKeywordAgainstLlm(data?.moments || []), [data?.moments])

  // Step 26 + 26.5: editor reject + per-video drop list. Uses the shared
  // useReject hook + RejectMomentDialog / RejectUndoToast components so
  // ClipPreview, Caption Library, and ReelEditor share the same flow.
  // locallyDropped is this surface's optimistic-hide set.
  const [locallyDropped, setLocallyDropped] = useState(new Set())
  const reject = useReject({
    onLocalRemove: (m) => setLocallyDropped(prev => new Set(prev).add(dropFingerprint(m))),
    onLocalRestore: (m) => setLocallyDropped(prev => { const n = new Set(prev); n.delete(dropFingerprint(m)); return n }),
  })

  // Step 27 Option B: track approvals so the ✓ Approved button state
  // persists across page refresh. Seed from server on mount via the
  // /admin/approved-moments/fingerprints endpoint, then bump locally
  // on each approve/undo for instant UI feedback.
  const [locallyApproved, setLocallyApproved] = useState(new Set())
  useEffect(() => {
    let cancelled = false
    api.approvedMomentsFingerprints().then(r => {
      if (cancelled) return
      const seeded = new Set()
      for (const f of (r?.fingerprints || [])) {
        seeded.add(`${f.tbtv_id}|${f.start_ms}|${(f.title || '').trim().toLowerCase()}`)
      }
      setLocallyApproved(seeded)
    }).catch(e => console.warn('failed to seed approved fingerprints', e))
    return () => { cancelled = true }
  }, [api])
  const approve = useApprove({
    onLocalMark: (m) => setLocallyApproved(prev => new Set(prev).add(dropFingerprint(m))),
    onLocalUnmark: (m) => setLocallyApproved(prev => { const n = new Set(prev); n.delete(dropFingerprint(m)); return n }),
  })

  const moments = useMemo(
    () => dedupedMoments.filter(m => !locallyDropped.has(dropFingerprint(m))),
    [dedupedMoments, locallyDropped],
  )
  const inCart = (m) => reelCart?.has?.(m.tbtv_id, m.start_ms)

  return (
    <div>
      <div className="card" style={{ marginBottom: 12, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <Sparkles size={16} style={{ color: '#a78bfa' }} />
        <span style={{ fontSize: 13, fontWeight: 600 }}>Find Key Moments — top results across catalog</span>
        <div style={{ flex: 1 }} />
        <label style={{ fontSize: 12, color: '#888', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Min score:
          <select
            value={minScore}
            onChange={(e) => setMinScore(parseFloat(e.target.value))}
            style={{ padding: '4px 8px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit' }}
          >
            <option value={0.5}>0.5</option>
            <option value={0.6}>0.6</option>
            <option value={0.7}>0.7</option>
            <option value={0.8}>0.8</option>
            <option value={0.85}>0.85</option>
            <option value={0.9}>0.9</option>
            <option value={0}>any</option>
          </select>
        </label>
        <button onClick={() => setApplyOpen(true)} title="Run Mux Robots against selected videos"
          style={{ padding: '6px 12px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500 }}>
          <Wand2 size={13} /> Apply Robots
        </button>
        <button onClick={load} disabled={loading} title="Refresh"
          style={{ padding: '6px 10px', background: 'transparent', border: '1px solid #333', borderRadius: 4, color: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {loading ? <Loader2 size={13} className="spin" /> : <RefreshCw size={13} />}
        </button>
      </div>

      {/* Source-filter chip strip. Lets editors audit one detector layer
          (Mux / TBTV-AI keyword / TBTV-AI LLM) in isolation to evaluate
          quality. Counts come from the source_counts histogram. */}
      <div className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#888', marginRight: 4 }}>Filter by source:</span>
        <Chip
          label="All sources"
          active={selectedSources.size === 0}
          onClick={clearSources}
          color="#bfdbfe"
        />
        {SOURCE_META.map(s => {
          const count = data?.source_counts?.[s.key] || 0
          if (count === 0 && !selectedSources.has(s.key)) return null
          return (
            <Chip
              key={s.key}
              label={s.label}
              active={selectedSources.has(s.key)}
              onClick={() => toggleSource(s.key)}
              color={s.color}
              count={count}
            />
          )
        })}
        {selectedSources.size > 0 && (
          <button onClick={clearSources} style={{
            marginLeft: 6, padding: '2px 8px', fontSize: 11,
            background: 'transparent', border: 'none', color: '#888', cursor: 'pointer',
          }}>Clear</button>
        )}
      </div>

      {/* Type-filter chip strip. Grouped by sport profile so a mixed-sport
          catalog gets a filter chip for every possible moment_type without
          the editor having to switch the header profile dropdown. Counts
          come from the type_counts histogram (server-side, respects the
          min-score filter). A whole profile row hides when every one of
          its chips has zero count AND none are currently selected — keeps
          the strip tight for a single-sport catalog while still letting
          football types show up when a football video lands in view.
          Falls back to the legacy single-profile view when the profiles
          list hasn't loaded yet (typical during first paint). */}
      <div className="card" style={{ marginBottom: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: '#888', marginRight: 4 }}>Filter by type:</span>
          <Chip
            label="All"
            active={selectedTypes.size === 0}
            onClick={clearTypes}
            color="#bfdbfe"
            count={data?.moments?.length}
          />
          {selectedTypes.size > 0 && (
            <button onClick={clearTypes} style={{
              marginLeft: 6, padding: '2px 8px', fontSize: 11,
              background: 'transparent', border: 'none', color: '#888', cursor: 'pointer',
            }}>Clear</button>
          )}
        </div>
        {(() => {
          const groups = profiles.length > 0
            ? profiles.map(p => ({
                profile_key: p.profile_key,
                display_name: p.display_name,
                types: Array.isArray(p.moment_types) ? p.moment_types : [],
              }))
            : [{ profile_key: profileKey, display_name: null, types: TYPE_META }]
          return groups.map(g => {
            const visibleTypes = g.types.filter(t => {
              if (t.key === '(other)') return false
              const count = data?.type_counts?.[t.key] || 0
              return count > 0 || selectedTypes.has(t.key)
            })
            if (visibleTypes.length === 0) return null
            const activeProfile = g.profile_key === profileKey
            return (
              <div key={g.profile_key} style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                {g.display_name && (
                  <span style={{
                    fontSize: 10, minWidth: 68, color: activeProfile ? '#bfdbfe' : '#666',
                    fontWeight: activeProfile ? 600 : 500,
                    textTransform: 'uppercase', letterSpacing: 0.4,
                  }}>{g.display_name}</span>
                )}
                {visibleTypes.map(t => (
                  <Chip
                    key={`${g.profile_key}:${t.key}`}
                    label={t.label}
                    active={selectedTypes.has(t.key)}
                    onClick={() => toggleType(t.key)}
                    color={t.color}
                    count={data?.type_counts?.[t.key] || 0}
                  />
                ))}
              </div>
            )
          })
        })()}
      </div>

      {err && <div className="card" style={{ color: 'crimson', marginBottom: 12 }}>{err}</div>}

      {!err && !loading && moments.length === 0 && (
        <div className="card" style={{ color: '#888', textAlign: 'center', padding: 24 }}>
          No key moments at this threshold yet.
          <div style={{ marginTop: 8, fontSize: 12, maxWidth: 560, marginLeft: 'auto', marginRight: 'auto' }}>
            Mux Robots run automatically when a video's captions are indexed. To force a backfill of already-indexed assets, hit
            <code style={{ marginLeft: 4, marginRight: 4, padding: '1px 6px', background: '#222', borderRadius: 3 }}>POST /api/highlights/admin/robots-backfill?job_type=find_key_moments</code>
            from a terminal. Each job takes a few minutes to complete.
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: 12 }}>
        {moments.map((m, i) => (
          <div key={`${m.tbtv_id}-${m.ord}-${i}`} className="card" style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 10 }}>
              {m.thumbnail_url ? (
                <img src={m.thumbnail_url} alt="" style={{ width: 96, height: 54, objectFit: 'cover', borderRadius: 4, background: '#222', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 96, height: 54, background: '#222', borderRadius: 4, flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3, marginBottom: 4 }}>
                  {m.title || '(untitled moment)'}
                </div>
                <div style={{ fontSize: 11, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.title_text || m.tbtv_id}
                </div>
                <div style={{ fontSize: 11, color: '#666', marginTop: 2, fontFamily: 'monospace', display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                  <span>{fmtTime(m.start_ms)} → {fmtTime(m.end_ms)} · score {' '}
                    <span style={{ color: scoreColor(m.quality_score), fontWeight: 600 }}>
                      {m.quality_score != null ? m.quality_score.toFixed(2) : '—'}
                    </span>
                  </span>
                  {(() => {
                    const pill = sourcePill(m)
                    if (!pill) return null
                    return (
                      <span style={{
                        fontSize: 10, padding: '1px 6px', borderRadius: 10,
                        background: pill.bg, color: pill.color, fontWeight: 600,
                        fontFamily: 'inherit', whiteSpace: 'nowrap',
                      }}>{pill.label}</span>
                    )
                  })()}
                  {m.moment_type && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 10,
                      background: '#1f2937', color: '#bbb',
                      fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 0.4,
                    }}>{m.moment_type.replace('_', ' ')}</span>
                  )}
                  {m.ground_reason === 'llm_cue_citation' && (
                    <span title={`Grounded to ${Array.isArray(m.grounded_cue_ids) ? m.grounded_cue_ids.length : 0} cue${Array.isArray(m.grounded_cue_ids) && m.grounded_cue_ids.length === 1 ? '' : 's'}: ${Array.isArray(m.grounded_cue_ids) ? m.grounded_cue_ids.join(', ') : ''}`}
                      style={{
                        fontSize: 10, padding: '1px 6px', borderRadius: 10,
                        background: 'rgba(34,197,94,0.15)', color: '#22c55e', fontWeight: 600,
                        fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: 'help',
                      }}>🔗 grounded</span>
                  )}
                  {m.ground_reason === 'keyword_match' && Array.isArray(m.grounded_cue_ids) && m.grounded_cue_ids[0] != null && (
                    <span title={`Keyword matched cue_id ${m.grounded_cue_ids[0]}`}
                      style={{
                        fontSize: 10, padding: '1px 6px', borderRadius: 10,
                        background: 'rgba(167,139,250,0.15)', color: '#a78bfa', fontWeight: 600,
                        fontFamily: 'inherit', whiteSpace: 'nowrap', cursor: 'help',
                      }}>🔗 cue {m.grounded_cue_ids[0]}</span>
                  )}
                </div>
              </div>
            </div>

            {(m.audible_narrative || m.visual_narrative) && (
              <div style={{ fontSize: 12, color: '#aaa', lineHeight: 1.5 }}>
                {m.audible_narrative && <div><span style={{ color: '#666' }}>🔊 </span>{m.audible_narrative}</div>}
                {m.visual_narrative && <div><span style={{ color: '#666' }}>👁 </span>{m.visual_narrative}</div>}
              </div>
            )}

            {Array.isArray(m.concepts) && m.concepts.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {m.concepts.slice(0, 8).map((c, idx) => (
                  <span key={idx} style={{ fontSize: 10, padding: '1px 6px', background: '#1f2937', borderRadius: 3, color: '#bbb' }}>{c}</span>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
              <button
                onClick={() => setPreviewing({ tbtv_id: m.tbtv_id, startMs: m.start_ms, endMs: m.end_ms, title: m.title || m.title_text, moment: m })}
                style={{ padding: '4px 10px', background: '#3b82f6', border: 'none', borderRadius: 4, color: 'white', cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <Play size={11} /> Preview
              </button>
              <button
                onClick={() => inCart(m)
                  ? reelCart?.remove?.(m.tbtv_id, m.start_ms)
                  : reelCart?.add?.({
                      tbtv_id: m.tbtv_id, start_ms: m.start_ms,
                      // Step 22: carry the moment's end_ms through to the
                      // reel builder so the segment window respects the
                      // moment's full span instead of a fixed ±10s.
                      end_ms: m.end_ms,
                      // Context for smart reel title. moment_type lets the
                      // builder summarize what kinds of moments are in the cart.
                      _origin: {
                        kind: 'key_moment',
                        moment_type: m.moment_type,
                        source: m.source,         // 'mux' | 'tbtv_ai'
                        detector: m.detector,     // 'keyword' | 'llm' | null
                        types_filter: selectedTypes.size > 0 ? Array.from(selectedTypes) : null,
                        sources_filter: selectedSources.size > 0 ? Array.from(selectedSources) : null,
                        min_score: minScore,
                      },
                    })
                }
                title={inCart(m) ? 'Remove from reel' : 'Add to reel'}
                style={{
                  padding: '4px 10px',
                  background: inCart(m) ? '#1e3a8a' : 'transparent',
                  border: '1px solid ' + (inCart(m) ? '#3b82f6' : '#333'),
                  borderRadius: 4,
                  color: inCart(m) ? '#bfdbfe' : 'inherit',
                  cursor: 'pointer', fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4,
                }}>
                {inCart(m) ? <Check size={11} /> : <Plus size={11} />}
                {inCart(m) ? 'In reel' : 'Add to reel'}
              </button>
              {/* Step 27 Option B: Approve. One-click, no dialog. Feeds
                  approved_moments → eval gold set + future Step 29 RAG. */}
              <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6 }}>
                <ApproveButton
                  onClick={() => approve.start(m)}
                  marked={locallyApproved.has(dropFingerprint(m))}
                  disabled={approve.busy}
                />
                {/* Step 26 + 26.5: Reject. Opens reason-category dialog,
                    then drops + hides + offers Undo. */}
                <RejectButton onClick={() => reject.start(m)} />
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* Step 26 + 26.5: shared reject dialog + undo toast. */}
      <RejectMomentDialog hook={reject} />
      <RejectUndoToast hook={reject} />
      {/* Step 27 Option B: approve undo toast (positioned above reject so
          both can be visible if both fire in quick succession). */}
      <ApproveUndoToast hook={approve} offsetBottom={80} />

      {previewing && (
        <ClipPreview
          tbtv_id={previewing.tbtv_id}
          startMs={previewing.startMs}
          endMs={previewing.endMs}
          title={previewing.title}
          onReject={previewing.moment ? () => reject.start(previewing.moment) : null}
          onApprove={previewing.moment ? () => approve.start(previewing.moment) : null}
          onClose={() => setPreviewing(null)}
        />
      )}

      {applyOpen && (
        <RobotsApplyModal
          onClose={() => {
            setApplyOpen(false)
            // Refresh the moments list — newly-queued jobs won't show up
            // until their webhooks fire, but completed-and-just-applied
            // re-runs will appear.
            load()
          }}
        />
      )}

      <style>{`@keyframes spin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } } .spin { animation: spin 1s linear infinite; }`}</style>
    </div>
  )
}

function Chip({ label, active, onClick, color, count }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '4px 10px',
        background: active ? color : 'transparent',
        border: '1px solid ' + (active ? color : '#333'),
        borderRadius: 999,
        color: active ? '#000' : color,
        cursor: 'pointer',
        fontSize: 12,
        fontWeight: 500,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
      }}>
      <span>{label}</span>
      {count != null && count > 0 && (
        <span style={{
          fontSize: 10,
          opacity: 0.8,
          padding: '0 4px',
          borderRadius: 999,
          background: active ? 'rgba(0,0,0,0.25)' : 'rgba(255,255,255,0.06)',
        }}>{count}</span>
      )}
    </button>
  )
}
